import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useDrag, useDrop } from 'react-dnd';
import { selectSelectedComponent, updateComponent } from '../../../redux/editorSlice';
import { 
  fetchProductCategories, 
  selectProductCategories, 
  selectProductCategoriesLoading 
} from '../../../redux/WithcookieSlice';
import { SkinSelector } from '../CommonProperties';
import '../PropertyPanel.css'


const CategoryMenuProperties = () => {
  const selectedComponent = useSelector(selectSelectedComponent);
  const dispatch = useDispatch();
  
  // 모달 상태 - early return 전에 선언
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMenuItem, setModalMenuItem] = useState(null);
  
  // 카테고리 목록 가져오기
  const productCategories = useSelector(selectProductCategories) || [];
  const isLoadingCategories = useSelector(selectProductCategoriesLoading);
  
  // 상품 카테고리 목록 가져오기 (회사 정보는 토큰에서 자동 추출)
  useEffect(() => {
    dispatch(fetchProductCategories());
  }, [dispatch]);
  

  
  // 컴포넌트가 없을 경우 early return
  if (!selectedComponent) {
    return null;
  }
  
  // 속성 값 가져오기
  const {
    menuItems = [],
    showProductCount = false,
    showIcon = false,
    showDescription = false,
    showCategoryInfo = true,
    layout = 'list',
    columns = 3,
    emptyMessage = '메뉴가 없습니다.'
  } = selectedComponent.componentProps || {};

  // 컴포넌트 속성 변경 핸들러
  const updateComponentProperty = (path, value) => {
    const pathParts = path.split('.');
    const existingProps = JSON.parse(JSON.stringify(selectedComponent.componentProps || {}));
    
    // 중첩된 경로 처리
    let target = existingProps;
    for (let i = 0; i < pathParts.length - 1; i++) {
      if (!target[pathParts[i]]) {
        target[pathParts[i]] = {};
      }
      target = target[pathParts[i]];
    }
    target[pathParts[pathParts.length - 1]] = value;
    
    dispatch(updateComponent({
      id: selectedComponent.id,
      changes: {
        componentProps: existingProps
      }
    }));
  };

  // 메뉴 아이템 추가
  const addMenuItem = () => {
    const newItem = {
      id: `menu-${Date.now()}`,
      categoryId: 0,
      categoryName: '카테고리를 선택하세요',
      routingPath: '/new-category', // 기본값 설정, 카테고리 선택 시 자동 변경됨
      parentId: null,
      order: menuItems.length,
      visible: true
    };
    
    const updatedMenuItems = [...menuItems, newItem];
    updateComponentProperty('menuItems', updatedMenuItems);
  };

  // 메뉴 아이템 삭제
  const removeMenuItem = (itemId) => {
    // 해당 아이템과 모든 하위 아이템들을 찾아서 삭제
    const getAllDescendants = (parentId) => {
      const children = menuItems.filter(item => item.parentId === parentId);
      return children.reduce((acc, child) => {
        return [...acc, child.id, ...getAllDescendants(child.id)];
      }, []);
    };
    
    const itemsToRemove = [itemId, ...getAllDescendants(itemId)];
    const updatedMenuItems = menuItems.filter((item) => !itemsToRemove.includes(item.id));
    updateComponentProperty('menuItems', updatedMenuItems);
  };

  // 메뉴 아이템 업데이트
  const updateMenuItem = (itemId, field, value) => {
    const updatedMenuItems = menuItems.map((item) => {
      if (item.id === itemId) {
        return { ...item, [field]: value };
      }
      return item;
    });
    updateComponentProperty('menuItems', updatedMenuItems);
  };

  // 메뉴 아이템 일괄 업데이트 (드래그 앤 드롭용)
  const updateMenuItems = (updatedItems) => {
    updateComponentProperty('menuItems', updatedItems);
  };

  // 모달 열기
  const openModal = (item) => {
    setModalMenuItem({ ...item });
    setIsModalOpen(true);
  };

  // 모달 닫기
  const closeModal = () => {
    setIsModalOpen(false);
    setModalMenuItem(null);
  };

  // 모달에서 저장
  const saveModalChanges = () => {
    if (!modalMenuItem) return;
    
    const updatedMenuItems = menuItems.map((item) => {
      if (item.id === modalMenuItem.id) {
        return modalMenuItem;
      }
      return item;
    });
    updateComponentProperty('menuItems', updatedMenuItems);
    closeModal();
  };

  // 계층 구조로 메뉴 변환
  const buildMenuTree = (items) => {
    const tree = [];
    const itemMap = {};
    
    // 먼저 모든 아이템을 맵에 저장
    items.forEach(item => {
      itemMap[item.id] = { ...item, children: [] };
    });
    
    // 부모-자식 관계 설정
    items.forEach(item => {
      if (item.parentId && itemMap[item.parentId]) {
        itemMap[item.parentId].children.push(itemMap[item.id]);
      } else {
        tree.push(itemMap[item.id]);
      }
    });
    
    // order 기준으로 정렬
    const sortByOrder = (items) => {
      items.sort((a, b) => a.order - b.order);
      items.forEach(item => {
        if (item.children?.length > 0) {
          sortByOrder(item.children);
        }
      });
    };
    
    sortByOrder(tree);
    return tree;
  };


  // 카테고리 ID로 카테고리 찾기 (재귀)
  const findCategoryById = (categories, targetId) => {
    for (const category of categories) {
      if (category.id === targetId) {
        return category;
      }
      if (category.children && category.children.length > 0) {
        const found = findCategoryById(category.children, targetId);
        if (found) return found;
      }
    }
    return null;
  };

  // 카테고리를 계층구조로 렌더링하는 헬퍼 함수
  const renderCategoryOptions = (categories, level = 0) => {
    const options = [];
    
    categories.forEach((category) => {
      // 들여쓰기를 위한 접두사
      const prefix = '\u00A0\u00A0'.repeat(level); // non-breaking space
      
      options.push(
        <option key={category.id} value={String(category.id)}>
          {prefix}{level > 0 ? '└ ' : ''}{category.name}
        </option>
      );
      
      // 하위 카테고리가 있으면 재귀적으로 렌더링
      if (category.children && category.children.length > 0) {
        options.push(...renderCategoryOptions(category.children, level + 1));
      }
    });
    
    return options;
  };
  // 메뉴 트리 구성
  const menuTree = buildMenuTree(menuItems);
  
  return (
    <div className="component-properties">
      {/* 스킨 선택 */}
      <SkinSelector 
        selectedComponent={selectedComponent}
        onSkinChange={(skinId) => updateComponentProperty('skin', skinId)}
      />
      
      {/* 메뉴 아이템 설정 */}
      <div className="property-section">
        <div className="property-section-header">
          <h3 className="property-section-title">메뉴 아이템 설정</h3>
          <button 
            className="add-menu-item-btn"
            onClick={addMenuItem}
          >
            + 메뉴 추가
          </button>
        </div>
        
        {menuItems.length === 0 ? (
          <div className="empty-menu-items">
            <p>설정된 메뉴가 없습니다. "메뉴 추가" 버튼을 클릭하여 메뉴를 추가하세요.</p>
          </div>
        ) : (
          <div className="menu-items-list">
            {menuTree.map((item) => (
              <MenuItem
                key={item.id}
                item={item}
                menuItems={menuItems}
                onUpdate={updateMenuItems}
                onEdit={openModal}
                onRemove={removeMenuItem}
                level={0}
              />
            ))}
            {menuTree.length === 0 && (
              <div className="empty-drop-area">
                메뉴가 없습니다. 메뉴를 추가하거나 드래그해서 이동하세요.
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* 표시 옵션 */}
      <div className="property-section">
        <h3 className="property-section-title">표시 옵션</h3>
        
        <div className="property-item">
          <label className="property-label">레이아웃 스타일</label>
          <select
            className="property-input"
            value={layout}
            onChange={(e) => updateComponentProperty('layout', e.target.value)}
          >
            <option value="list">리스트</option>
            <option value="grid">그리드</option>
            <option value="dropdown">드롭다운</option>
          </select>
        </div>
        
        {layout === 'grid' && (
          <div className="property-item">
            <label className="property-label">그리드 열 개수</label>
            <select
              className="property-input"
              value={columns}
              onChange={(e) => updateComponentProperty('columns', parseInt(e.target.value))}
            >
              <option value={2}>2열</option>
              <option value={3}>3열</option>
              <option value={4}>4열</option>
              <option value={6}>6열</option>
            </select>
          </div>
        )}
        
        <div className="toggle-option">
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={showProductCount}
              onChange={(e) => updateComponentProperty('showProductCount', e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
          <div>
            <div className="toggle-label-text">상품 개수 표시</div>
            <small className="property-help">
              ⚠️ 상품 개수는 별도 API로 조회해야 하므로 현재 지원되지 않습니다
            </small>
          </div>
        </div>
        
        <div className="toggle-option">
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={showIcon}
              onChange={(e) => updateComponentProperty('showIcon', e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
          <span className="toggle-label-text">아이콘 표시</span>
        </div>
        
        <div className="toggle-option">
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={showDescription}
              onChange={(e) => updateComponentProperty('showDescription', e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
          <span className="toggle-label-text">설명 표시</span>
        </div>
        
        <div className="toggle-option">
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={showCategoryInfo}
              onChange={(e) => updateComponentProperty('showCategoryInfo', e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
          <div>
            <div className="toggle-label-text">카테고리 정보 표시</div>
            <small className="property-help">라우팅 시 카테고리 ID와 정보를 표시합니다</small>
          </div>
        </div>
      </div>
      
      {/* 기타 설정 */}
      <div className="property-section">
        <h3 className="property-section-title">기타 설정</h3>
        
        <div className="property-item">
          <label className="property-label">빈 메뉴 메시지</label>
          <input
            type="text"
            className="property-input"
            value={emptyMessage}
            onChange={(e) => updateComponentProperty('emptyMessage', e.target.value)}
            placeholder="메뉴가 없습니다."
          />
          <small className="property-help">메뉴가 없을 때 표시할 메시지</small>
        </div>
      </div>
      
      {/* 모달 */}
      {isModalOpen && modalMenuItem && (
        <div className="menu-modal-overlay" onClick={closeModal}>
          <div className="menu-modal" onClick={(e) => e.stopPropagation()}>
            <div className="menu-modal-header">
              <h3 className="menu-modal-title">메뉴 항목 수정</h3>
              <button className="menu-modal-close" onClick={closeModal}>&times;</button>
            </div>
            
            <div className="menu-modal-content">
              <div className="property-item">
                <label className="property-label">카테고리 선택</label>
                {isLoadingCategories ? (
                  <div className="loading-message">카테고리 목록 불러오는 중...</div>
                ) : (
                  <select
                    className="property-input"
                    value={modalMenuItem.categoryId || ''}
                    onChange={(e) => {
                      const categoryId = Number(e.target.value);
                      const selectedCategory = findCategoryById(productCategories, categoryId);
                      if (selectedCategory) {
                        // 카테고리 이름을 URL 친화적으로 변환
                        const urlFriendlyName = selectedCategory.name
                          .toLowerCase()
                          .replace(/\s+/g, '-')
                          .replace(/[^가-힣A-Za-z0-9\-]/g, '')
                          .replace(/-+/g, '-')
                          .replace(/^-|-$/g, '');
                        
                        setModalMenuItem({
                          ...modalMenuItem,
                          categoryId,
                          categoryName: selectedCategory.name,
                          routingPath: `/${urlFriendlyName}`
                        });
                      }
                    }}
                  >
                    <option value="">카테고리를 선택하세요</option>
                    {renderCategoryOptions(productCategories)}
                  </select>
                )}
              </div>
              
              <div className="property-item">
                <label className="property-label">라우팅 경로</label>
                <div className="routing-path-display">
                  <span className="routing-full-path">/shopping{modalMenuItem.routingPath || '/'}</span>
                </div>
                <small className="property-help">
                  카테고리 선택 시 자동으로 생성됩니다
                </small>
              </div>
              
              <div className="property-item">
                <div className="toggle-option">
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={modalMenuItem.visible}
                      onChange={(e) => setModalMenuItem({
                        ...modalMenuItem,
                        visible: e.target.checked
                      })}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <span className="toggle-label-text">메뉴 표시</span>
                </div>
              </div>
            </div>
            
            <div className="menu-modal-actions">
              <button className="menu-modal-cancel" onClick={closeModal}>취소</button>
              <button className="menu-modal-save" onClick={saveModalChanges}>저장</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// 메뉴 아이템 컴포넌트
const MenuItem = ({ item, menuItems, onUpdate, onEdit, onRemove, level }) => {
  const ref = useRef(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [dropPosition, setDropPosition] = useState(null);
  
  // 드래그 설정
  const [{ isDragging }, drag] = useDrag({
    type: 'menu-item',
    item: () => {
      // 드래그 시작 시 현재 상태의 아이템 정보 반환
      const currentItem = menuItems.find(i => i.id === item.id);
      return { id: item.id, parentId: currentItem?.parentId || null };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  });
  
  // 드롭 설정
  const [{ isOver }, drop] = useDrop({
    accept: 'menu-item',
    hover: (draggedItem, monitor) => {
      if (!ref.current) return;
      if (draggedItem.id === item.id) return;
      
      const draggedId = draggedItem.id;
      const targetId = item.id;
      
      // 자기 자신의 자식으로는 이동 불가
      const isDescendant = (parentId, childId) => {
        const findChildren = (id) => {
          const children = menuItems.filter(i => i.parentId === id);
          return children.reduce((acc, child) => {
            return [...acc, child.id, ...findChildren(child.id)];
          }, []);
        };
        return findChildren(parentId).includes(childId);
      };
      
      if (isDescendant(draggedId, targetId)) return;
      
      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      
      // 위치에 따라 드롭 위치 결정
      if (hoverClientY < hoverMiddleY / 2) {
        // 위쪽 25% 영역: 이전 메뉴로 추가
        setDropPosition('before');
      } else if (hoverClientY > hoverMiddleY * 1.5) {
        // 아래쪽 25% 영역: 다음 메뉴로 추가
        setDropPosition('after');
      } else {
        // 중간 50% 영역: 하위 메뉴로 추가 (최대 3단계까지만)
        if (level < 2) {
          setDropPosition('child');
        } else {
          setDropPosition(null);
        }
      }
    },
    drop: (draggedItem, monitor) => {
      if (!dropPosition) return;
      if (monitor.didDrop()) return;
      
      const draggedId = draggedItem.id;
      const targetId = item.id;
      
      // 위치에 따라 부모-자식 관계 설정
      const updatedItems = menuItems.map(item => ({ ...item })); // 깊은 복사
      const draggedIndex = updatedItems.findIndex(i => i.id === draggedId);
      const targetIndex = updatedItems.findIndex(i => i.id === targetId);
      
      if (draggedIndex === -1 || targetIndex === -1) return;
      
      const draggedMenuItem = updatedItems[draggedIndex];
      
      // 드롭 위치에 따라 처리
      if (dropPosition === 'child') {
        // 하위 메뉴로 이동
        draggedMenuItem.parentId = targetId;
        const targetChildren = updatedItems.filter(i => i.parentId === targetId);
        draggedMenuItem.order = targetChildren.length;
      } else if (dropPosition === 'before' || dropPosition === 'after') {
        // 같은 레벨에서 순서 변경
        draggedMenuItem.parentId = item.parentId;
        
        // 형제 메뉴들 가져오기
        const siblings = updatedItems.filter(i => 
          i.parentId === item.parentId && i.id !== draggedId
        ).sort((a, b) => a.order - b.order);
        
        // 대상 메뉴의 인덱스 찾기
        const targetSiblingIndex = siblings.findIndex(s => s.id === targetId);
        
        if (dropPosition === 'before') {
          draggedMenuItem.order = targetSiblingIndex - 0.5;
        } else {
          draggedMenuItem.order = targetSiblingIndex + 1.5;
        }
      }
      
      // order 재정렬
      const reorderItems = (items, parentId) => {
        const siblings = items
          .filter(i => i.parentId === parentId)
          .sort((a, b) => a.order - b.order);
        
        siblings.forEach((sibling, index) => {
          sibling.order = index;
        });
      };
      
      // 모든 부모별로 order 재정렬
      const allParentIds = [...new Set(updatedItems.map(i => i.parentId))];
      allParentIds.forEach(parentId => {
        reorderItems(updatedItems, parentId);
      });
      
      onUpdate(updatedItems);
      setDropPosition(null);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true })
    })
  }, [dropPosition, level, menuItems]);
  
  // 드래그 & 드롭 연결
  drag(drop(ref));
  
  const hasChildren = item.children && item.children.length > 0;
  
  // 드롭 위치에 따른 클래스 설정
  const getDropPositionClass = () => {
    if (!isOver || !dropPosition) return '';
    return `drop-${dropPosition}`;
  };
  
  return (
    <div className="menu-item-wrapper">
      <div
        ref={ref}
        id={`menu-item-${item.id}`}
        className={`menu-item-config ${isDragging ? 'dragging' : ''} ${isOver ? 'over' : ''} ${getDropPositionClass()}`}
        style={{ marginLeft: `${level * 20}px` }}
        onDoubleClick={() => onEdit(item)}
      >
        <div className="menu-item-header">
          <div className="menu-item-title">
            {hasChildren && (
              <button
                className="menu-item-expand"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {isExpanded ? '▼' : '▶'}
              </button>
            )}
            <span>{item.categoryName}</span>
            {!item.visible && <span style={{ color: '#999', fontSize: '12px' }}> (숨김)</span>}
          </div>
          <button 
            className="menu-item-remove" 
            onClick={() => onRemove(item.id)}
            title="삭제"
          >
            ×
          </button>
        </div>
      </div>
      
      {hasChildren && isExpanded && (
        <div className="menu-item-children">
          {item.children.map((child) => (
            <MenuItem
              key={child.id}
              item={child}
              menuItems={menuItems}
              onUpdate={onUpdate}
              onEdit={onEdit}
              onRemove={onRemove}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CategoryMenuProperties;